import { useEffect, useState, type ReactNode } from "react";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { useAuthStore } from "@safers/auth";
import { createQueryClient } from "./client";

interface QueryProviderProps {
  children: ReactNode;
  /** 개발 환경에서만 Devtools 노출. 각 앱이 import.meta.env.DEV 를 전달한다. */
  devtools?: boolean;
}

/**
 * 공통 서버 상태 프로바이더. main.tsx 에서 AuthProvider 안쪽에 배치한다.
 * QueryClient 는 마운트당 1회만 생성(useState 초기화 함수).
 */
export function QueryProvider({ children, devtools = false }: QueryProviderProps) {
  const [client] = useState(createQueryClient);

  // 로그아웃/401 리셋으로 user가 null이 되면 이전 계정의 캐시를 제거한다.
  // 같은 탭에서 계정 전환 시 이전 계정 서버 데이터가 노출되는 누출을 막는다(401 자동 로그아웃 포함).
  useEffect(() => {
    let prevUser = useAuthStore.getState().user;
    return useAuthStore.subscribe((state) => {
      if (prevUser && !state.user) client.clear();
      prevUser = state.user;
    });
  }, [client]);

  return (
    <QueryClientProvider client={client}>
      {children}
      {devtools ? <ReactQueryDevtools initialIsOpen={false} /> : null}
    </QueryClientProvider>
  );
}
